/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import styles from "./Chat.module.scss";
import { useCurrentTrip, useResponsive } from "@/hooks";
import { useTwilioConversation } from "./hooks";
import ConversationsList from "./ConversationsList";
import Conversation from "./Conversation";
import NewConversation from "./NewConversation";
import Typography from "@/components/Typography";
import Badge from "@/components/Badge";
import { IconButton } from "@/components/Button";
import {
  ArrowIcon,
  ChevronIcon,
  NewIcon,
  MoreIcon,
  BinIcon,
} from "@/components/Icon";
import Divider from "@/components/Divider";
import { CircularProgress } from "@/components/Loader";
import cx from "classnames";
import { MobileModal } from "@/components/Modal";
import { MobilePaperTitle } from "@/components/PageTitle";
import { usePublicProfileQuery } from "@/fetch/profiles/traveller";
import { SocialAvatar, TravellerNameLink } from "@/components/Social";
import { useGroupQuery } from "@/fetch/social";
import Menu, { MenuItem } from "@/components/Menu";
import { ListItemIcon, ListItemText } from "@/components/List";
import Tooltip from "../Tooltip";
import SuggestionList from "./SuggestionList";
import { useRouter } from "next/router";
import MobileConversationDeleteConfirmationDialog from "./MobileConversationDeleteConfirmationDialog";
import Flag from "@/components/Flag";

const Chat: React.FC<{
  isOpen: boolean;
  onTogglePopover?: () => void;
  twoColumnLayout?: boolean;
  showUnreadMessagesBadge?: boolean;
  showBackButton?: boolean;
}> = ({
  isOpen,
  onTogglePopover,
  twoColumnLayout = false,
  showUnreadMessagesBadge = true,
  showBackButton = false,
}) => {
  const {
    state,
    isConversationsLoading,
    updateConversations,
    toggleChatSidebar,
    currentConversationSid,
    setCurrentConversationSid,
    overlayLoading,
    changePopoverState,
    sessionsToDisplay,
    totalUnreadConversationCount,
    setShowTravellersList,
    showTravellersList,
    leaveConversation,
    deletingConversationSid,
  } = useTwilioConversation();
  const router = useRouter();
  const { isMobile } = useResponsive();
  const { currentTrip } = useCurrentTrip();
  const thisUserCid = currentTrip?.cid;
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  const [isWarnDiagOpen, setIsWarnDialogOpen] = useState(false);

  useEffect(() => {
    /* update conversation every time the chat has opened */
    if (isOpen && !isConversationsLoading)
      updateConversations(!state?.sessions?.length);
  }, [isOpen]);

  useEffect(() => {
    /* initially update the conversations*/
    updateConversations(!state?.sessions?.length);
  }, []);

  useEffect(() => {
    if (!isOpen) setShowTravellersList?.(false);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (isMobile) return;
      setCurrentConversationSid?.(undefined);
      changePopoverState(false);
    };
  }, []);

  const hasConnectionError = state?.connectionStatus === "error";
  const showMessagesPage =
    currentConversationSid !== undefined &&
    !showTravellersList &&
    !hasConnectionError;

  const showConversationsList = !currentConversationSid && !showTravellersList;

  const showNewMessagePage = showTravellersList && !currentConversationSid;

  const isTwoColumnLayout = twoColumnLayout && !isMobile;

  const onCreateNewConversationClicked = () => {
    if (isOpen) {
      toggleChatSidebar();
    }
    setCurrentConversationSid?.(undefined);
    setShowTravellersList?.(true);
  };

  const overlayLoadingLayer = (
    <div className={styles.overlayLoader}>
      <CircularProgress />
    </div>
  );

  const newConversationList = (
    <NewConversation onExit={() => setShowTravellersList?.(false)} />
  );

  const conversationsListComponent = (
    <>
      <div
        role="button"
        className={cx(styles.header, {
          [styles.clickableChatHeader]: Boolean(onTogglePopover),
        })}
        onClick={() => onTogglePopover?.()}
      >
        {showBackButton && (
          <IconButton onClick={() => router.back()}>
            <ArrowIcon dir="left" />
          </IconButton>
        )}
        <Typography
          variant="subtitle1"
          component="button"
          className={styles.title}
        >
          Chat
          <Badge
            badgeContent={totalUnreadConversationCount}
            color="error"
            componentsProps={{ badge: { className: styles.badge } }}
            invisible={
              !Boolean(totalUnreadConversationCount) || !showUnreadMessagesBadge
            }
          />
        </Typography>
        <Tooltip title="New message" placement="top" arrow>
          <IconButton
            disabled={hasConnectionError}
            className={styles.newConversationButton}
            onClick={onCreateNewConversationClicked}
          >
            <NewIcon />
          </IconButton>
        </Tooltip>
        {Boolean(onTogglePopover) && (
          <IconButton className={styles.collapseButton}>
            <ChevronIcon dir={isOpen ? "down" : "up"} />
          </IconButton>
        )}
      </div>
      <Divider className={styles.divider} />
      <ConversationsList
        hasError={hasConnectionError}
        onReload={() => updateConversations(true)}
        sessions={sessionsToDisplay}
        isConversationsLoading={isConversationsLoading}
        onConversationClick={(sid) => setCurrentConversationSid?.(sid)}
        selectedConversationSid={currentConversationSid}
      />
    </>
  );

  const { data: group } = useGroupQuery();
  const session = state?.sessions.find(
    (item) => item?.conversationSid === currentConversationSid
  );
  const peerCid = session?.travellerInfo?.cid;
  const { data: publicProfile } = usePublicProfileQuery({ cid: peerCid });
  const userProfile = publicProfile?.[0];
  const profilePicturePath = userProfile?.s3_profile_pic;
  const isAdmin =
    Boolean(
      userProfile?.is_admins?.find((item) => item.group_id === group?.id)
        ?.is_admin
    ) || false;
  const isSuspended =
    Boolean(
      userProfile?.is_admins?.find((item) => item?.group_id === group?.id)
        ?.is_suspended
    ) || false;
  const adminTitle =
    userProfile?.is_admins?.find((item) => item.group_id === group?.id)
      ?.admin_title || null;
  const isTyping = session?.participant?.isTyping;

  const conversationComponent = (
    <Conversation
      onTogglePopover={onTogglePopover}
      conversationSid={currentConversationSid || ""}
      onExit={() => setCurrentConversationSid?.(undefined)}
    />
  );

  const isConversationDeleting =
    deletingConversationSid === session?.conversationSid;

  if (isTwoColumnLayout) {
    return (
      <div className={styles.chatRootTwoColumnLayout}>
        {Boolean(overlayLoading) && overlayLoadingLayer}
        <div className={styles.leftContainer}>
          {!showTravellersList && conversationsListComponent}
          {showTravellersList && newConversationList}
        </div>
        <Divider orientation="vertical" />
        <div className={styles.rightContainer}>
          {showMessagesPage && conversationComponent}
          {!showMessagesPage && (
            <div className={styles.notSelectedConversation}>
              <Typography variant="body1" color="text.secondary">
                Start a new conversation <br />
                or continue an existing one
              </Typography>
            </div>
          )}
        </div>
      </div>
    );
  }

  const menuList = [
    {
      title: "Delete chat",
      icon: <BinIcon color="inherit" />,
      action: () => {
        setIsWarnDialogOpen(true);
        setMenuAnchorEl(null);
      },
    },
  ];

  const removeChat = async () => {
    // remove user from the conversation and back to the conversations list
    try {
      if (!session?.conversationSid) return;
      await leaveConversation(session?.conversationSid, peerCid);
      setCurrentConversationSid?.(undefined);
    } catch (error) {
      console.error(error);
    }
    setIsWarnDialogOpen(false);
  };

  const mobileChatHeader = (
    <div className={styles.chatHeaderContainer}>
      <SocialAvatar
        cloudinaryPath={profilePicturePath}
        isAdmin={isAdmin}
        adminTitle={adminTitle}
        size="s"
        className={styles.avatar}
      />
      <span className={styles.nameContainer}>
        <TravellerNameLink
          variant="body1"
          cid={session?.travellerInfo?.cid}
          nickname={session?.travellerInfo?.name}
        />
        {Boolean(userProfile?.country_code) && !isAdmin && (
          <Flag
            countryCode={userProfile?.country_code || ""}
            className={styles.flag}
            size="1.5em"
          />
        )}
        {isTyping && (
          <Typography variant="body2" color="text.secondary">
            is typing...
          </Typography>
        )}
      </span>
      <IconButton
        className={styles.moreButton}
        onClick={(event) => setMenuAnchorEl(event.currentTarget)}
      >
        <MoreIcon />
      </IconButton>
      <Menu
        elevation={3}
        id="conversation-menu"
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={() => setMenuAnchorEl(null)}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        {menuList?.map((item) => (
          <MenuItem key={item.title} onClick={item.action}>
            <ListItemIcon sx={{ color: "text.primary" }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText>{item.title}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );

  return (
    <>
      {isMobile ? (
        <>
          {isOpen && (
            <MobileModal>
              <MobilePaperTitle
                onGoBack={() => {
                  if (showNewMessagePage) {
                    setShowTravellersList?.(false);
                  } else if (showMessagesPage) {
                    setCurrentConversationSid?.(undefined);
                  } else {
                    onTogglePopover?.();
                    document.body.style.overflow = "auto";
                  }
                }}
                title={
                  showConversationsList
                    ? "Messages"
                    : showNewMessagePage
                    ? "New message"
                    : mobileChatHeader
                }
                iconType={showNewMessagePage ? "close" : "back"}
                titleClassName={styles.titleContainer}
              >
                {showConversationsList && (
                  <IconButton
                    onClick={onCreateNewConversationClicked}
                    className={styles.mobileNewChatButton}
                  >
                    <NewIcon />
                  </IconButton>
                )}
              </MobilePaperTitle>
              {Boolean(overlayLoading) && overlayLoadingLayer}

              {showMessagesPage && conversationComponent}
              {showNewMessagePage && newConversationList}
              {showConversationsList && (
                <>
                  <SuggestionList />
                  <ConversationsList
                    hasError={hasConnectionError}
                    onReload={() => updateConversations(true)}
                    sessions={sessionsToDisplay}
                    isConversationsLoading={isConversationsLoading}
                    onConversationClick={(sid) =>
                      setCurrentConversationSid?.(sid)
                    }
                    selectedConversationSid={currentConversationSid}
                  />
                </>
              )}
              {isWarnDiagOpen && (
                <MobileConversationDeleteConfirmationDialog
                  onClose={() => setIsWarnDialogOpen(false)}
                  isDeleting={isConversationDeleting}
                  onDelete={removeChat}
                />
              )}
            </MobileModal>
          )}
        </>
      ) : (
        <div className={styles.chatRoot}>
          {Boolean(overlayLoading) && overlayLoadingLayer}

          {showMessagesPage && conversationComponent}
          {showNewMessagePage && newConversationList}
          {showConversationsList && conversationsListComponent}
        </div>
      )}
    </>
  );
};

export default Chat;
